import React from 'react';
import { useLazyImage } from '../hooks/useLazyImage';
import './BackgroundPreview.css';

interface BackgroundOption {
  value: string;
  name: string;
  imagePath?: string;
  thumbnailPath?: string;
}

interface BackgroundPreviewProps {
  value: string;
  onChange: (value: string) => void;
}

interface BackgroundGroup {
  title: string;
  options: BackgroundOption[];
}

const backgroundGroups: BackgroundGroup[] = [
  {
    title: 'Light Backgrounds',
    options: [
      { 
        value: 'light-1', 
        name: 'Marble', 
        imagePath: '/bg-light-1.jpg',
        thumbnailPath: '/thumbnails/bg-light-1.jpg'
      },
      { 
        value: 'light-2', 
        name: 'Ocean Shore', 
        imagePath: '/bg-light-2.jpg',
        thumbnailPath: '/thumbnails/bg-light-2.jpg'
      },
      { 
        value: 'light-3', 
        name: 'Soft Clouds', 
        imagePath: '/bg-light-3.jpg',
        thumbnailPath: '/thumbnails/bg-light-3.jpg'
      },
      { 
        value: 'light-4', 
        name: 'Cracked Earth', 
        imagePath: '/bg-light-4.jpg',
        thumbnailPath: '/thumbnails/bg-light-4.jpg'
      },
      { 
        value: 'light-5', 
        name: 'Stucco Wall', 
        imagePath: '/bg-light-5.jpg',
        thumbnailPath: '/thumbnails/bg-light-5.jpg'
      },
    ]
  },
  {
    title: 'Dark Backgrounds',
    options: [
      { 
        value: 'dark-1', 
        name: 'Wood Grain', 
        imagePath: '/bg-dark-1.jpg',
        thumbnailPath: '/thumbnails/bg-dark-1.jpg'
      },
      { 
        value: 'dark-2', 
        name: 'Autumn Leaves', 
        imagePath: '/bg-dark-2.jpg',
        thumbnailPath: '/thumbnails/bg-dark-2.jpg'
      },
      { 
        value: 'dark-3', 
        name: 'Desert Sunset', 
        imagePath: '/bg-dark-3.jpg',
        thumbnailPath: '/thumbnails/bg-dark-3.jpg'
      },
      { 
        value: 'dark-4', 
        name: 'Leather', 
        imagePath: '/bg-dark-4.jpg',
        thumbnailPath: '/thumbnails/bg-dark-4.jpg'
      },
      { 
        value: 'dark-5', 
        name: 'Tropical Night', 
        imagePath: '/bg-dark-5.jpg',
        thumbnailPath: '/thumbnails/bg-dark-5.jpg'
      },
    ]
  }
];

// Lazy loading image component
interface LazyBackgroundImageProps {
  option: BackgroundOption;
  isSelected: boolean;
}

const LazyBackgroundImage: React.FC<LazyBackgroundImageProps> = ({ option, isSelected }) => {
  const { currentSrc, isLoading, hasError, preloadFull } = useLazyImage({
    thumbnailSrc: option.thumbnailPath || option.imagePath || '',
    fullSrc: option.imagePath || ''
  });

  // Preload full image when selected or on hover
  React.useEffect(() => {
    if (isSelected) {
      preloadFull();
    }
  }, [isSelected, preloadFull]);

  return (
    <div 
      className="background-preview-thumbnail"
      title={option.name}
      onMouseEnter={preloadFull}
    >
      {isLoading && (
        <div className="background-preview-loading">
          <div className="loading-spinner" />
        </div>
      )}
      <img 
        src={currentSrc}
        alt={option.name}
        className={`background-preview-image ${isLoading ? 'loading' : ''}`}
        onError={(e) => {
          // Fallback to thumbnail if full image fails
          if (currentSrc === option.imagePath && option.thumbnailPath) {
            (e.target as HTMLImageElement).src = option.thumbnailPath;
          }
        }}
      />
      {hasError && (
        <div className="background-preview-error">
          <span>Failed to load</span>
        </div>
      )}
    </div>
  );
};

export const BackgroundPreview: React.FC<BackgroundPreviewProps> = ({ value, onChange }) => {
  return (
    <div className="background-preview-container">
      {/* None option integrated with header */}
      <div className="background-preview-header">
        <div 
          className={`background-preview-none-option ${value === 'none' ? 'selected' : ''}`}
          onClick={() => onChange('none')}
        >
          <div className="background-preview-none-thumbnail">
            <span className="none-text">None</span>
          </div>
          <span className="background-preview-none-label">No Background</span>
        </div>
      </div>

      {backgroundGroups.map((group, groupIndex) => (
        <div key={groupIndex} className="background-preview-group">
          <h4 className="background-preview-group-title">{group.title}</h4>
          <div className="background-preview-grid">
            {group.options.map((option) => (
              <div
                key={option.value}
                className={`background-preview-option ${value === option.value ? 'selected' : ''}`}
                data-value={option.value}
                onClick={() => onChange(option.value)}
              >
                <LazyBackgroundImage 
                  option={option}
                  isSelected={value === option.value}
                />
                <span className="background-preview-label">{option.name}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};